// src/ui/steps/step2-niveau.jsx: Bevestig variant (3.1, alleen festival met
// varianten) en zwaarteniveau (3.2). Voor film/AV en festival-rollen zonder
// varianten valt 3.1 weg en wordt direct de niveau-pagina getoond.
import React from "react";
import { FW, FwButton, FwInlineBanner, StepHeader, AsideCard, RolTooltip } from "../primitives";
import { config } from "../../lib/config";
import { niveauIndicatorenFilmAv, sectieLabels } from "../../data/niveau-indicatoren";
import { bulletsVoorRol } from "../../data/rollen";
import { FAMILIE_CRITERIA, bepaalVariant, hasCriteria } from "../../lib/festival-criteria";
import { calc } from "../../lib/calc";

// Varianten A t/m E komen uit het Functiehandboek Filmfestivals 2.0.
// A is altijd de laagste schaal, hogere letter = hogere schaal. Het aantal
// varianten verschilt per familie (Medewerker: A/B; Hoofd: A/B/C; Directie:
// A t/m E). De keuze volgt uit de criteria-vragen of een directe variant-knop.
const VARIANT_LETTERS = ["A", "B", "C", "D", "E"];

// Capitaliseert een domein-label uit de config zodat samenstellingen als
// "tactisch/operationeel" als "Tactisch/Operationeel" worden getoond.
const capDomein = (s) =>
  (s || "").replace(/(^|[\s/])([a-zà-öø-ÿ])/g, (_, pre, c) => pre + c.toUpperCase());

export const StepNiveau = ({ state, setState, next, back, hideNext = false, subOverride }) => {
  const isFestival = state.type === "festival";
  const isClient = !isFestival && state.perspectief === "opdrachtgever";
  const tariefrijen = isFestival ? config.tarieven_festival : config.tarieven_film_av;
  const huidigeRol = state.rollen?.[0];
  const heeftVarianten = isFestival && !!huidigeRol?.heeftVarianten;

  // Sub-step state: 3.1 = 'variant' (alleen voor heeftVarianten), 3.2 = 'schaal'.
  // Bij re-entry uit een volgende step (bv. Terug vanuit Dienstverband) is er
  // al een niveau gekozen: land dan direct op 3.2 zodat de Terug-knop daar
  // teruggaat naar 3.1 (en niet voorbij 3.1 naar step1 springt).
  const [subStep, setSubStep] = React.useState(
    heeftVarianten && huidigeRol?.niveau == null ? "variant" : "schaal"
  );

  const [niveau, setNiveau] = React.useState(huidigeRol?.niveau);

  // Criteria-antwoorden voor de variant-substep. Initieel uit state zodat
  // re-entry (back vanuit 3.2 of via progress-bar) de antwoorden behoudt.
  // Wisselen tussen 3.1 ↔ 3.2 verliest ook geen data; en localStorage in
  // main.jsx persisteert tussen sessies. Wordt gewist bij rol-wisseling
  // (zie step1-rol bevestig-logic).
  const [hulpAntwoorden, setHulpAntwoorden] = React.useState(
    huidigeRol?.criteriaAntwoorden || {}
  );

  // Sub-familie-keuze (alleen voor families met subFamilies, bv. Hoofd
  // Programma vs Hoofd Educatie/Talent/Industry/Nieuwe Media). Bepaalt norm
  // en uitgesloten kolommen. Init: huidige rol-state → familie-default.
  const [subFamilieId, setSubFamilieId] = React.useState(() => {
    if (huidigeRol?.subFamilieId) return huidigeRol.subFamilieId;
    const fc = FAMILIE_CRITERIA[huidigeRol?.familie || ""];
    return fc?.subFamilies ? fc.defaultSubFamilieId : null;
  });

  // Antwoorden naar parent state pushen.
  React.useEffect(() => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, criteriaAntwoorden: hulpAntwoorden } : r
      ),
    }));
  }, [hulpAntwoorden]);

  // Sub-familie-keuze naar parent state pushen.
  React.useEffect(() => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, subFamilieId } : r
      ),
    }));
  }, [subFamilieId]);

  // Niveaus die de Gids Rollen aan deze rol toekent.
  const gidsNiveaus = React.useMemo(() => {
    if (!huidigeRol) return [];
    if (Array.isArray(huidigeRol.niveaus) && huidigeRol.niveaus.length) return huidigeRol.niveaus;
    return huidigeRol.niveau ? [huidigeRol.niveau] : [];
  }, [huidigeRol]);

  React.useEffect(() => {
    if (!huidigeRol) back();
  }, []);

  // Sync niveau-keuze direct naar parent state: bewaart selectie bij stap-wisselen
  React.useEffect(() => {
    if (!huidigeRol || niveau === undefined || niveau === huidigeRol.niveau) return;
    setState(s => {
      const r0 = s.rollen?.[0];
      // Detecteer: niveau is via variant-keuze geset (variant-schaal matcht).
      const isVariantKeuze = r0?.festivaltype
        && r0?.variant_schalen?.[r0.festivaltype] === niveau;
      const bron = isVariantKeuze ? "variant" : "handmatig";
      return {
        ...s,
        rollen: (s.rollen || []).map((r, i) =>
          i === 0 ? { ...r, niveau, niveau_bron: bron } : r
        ),
      };
    });
  }, [niveau]);

  if (!huidigeRol) return null;

  const familieCriteria = heeftVarianten && huidigeRol?.familie
    ? FAMILIE_CRITERIA[huidigeRol.familie]
    : null;
  const hulpResultaat = familieCriteria
    ? bepaalVariant(hulpAntwoorden, huidigeRol.familie, subFamilieId)
    : null;
  // keuze=null betekent deselecteren: verwijdert het criterium-antwoord uit state.
  const beantwoordCriterium = (criteriumId, keuze) => {
    setHulpAntwoorden(prev => {
      if (keuze == null) {
        const { [criteriumId]: _verwijderd, ...rest } = prev;
        return rest;
      }
      return { ...prev, [criteriumId]: keuze };
    });
  };
  // keuze=null deselecteert deze sub. Als daarna geen sub meer over is wordt
  // het hele criterium-antwoord verwijderd zodat de evaluator hem als
  // onbeantwoord ziet.
  const beantwoordSubCriterium = (criteriumId, subId, keuze) => {
    setHulpAntwoorden(prev => {
      const huidig = prev[criteriumId] || {};
      const huidigeSub = huidig.sub || {};
      let nieuweSub;
      if (keuze == null) {
        const { [subId]: _verwijderd, ...rest } = huidigeSub;
        nieuweSub = rest;
      } else {
        nieuweSub = { ...huidigeSub, [subId]: keuze };
      }
      if (Object.keys(nieuweSub).length === 0) {
        const { [criteriumId]: _verwijderd, ...rest } = prev;
        return rest;
      }
      return { ...prev, [criteriumId]: { ...huidig, sub: nieuweSub } };
    });
  };

  // Variant-keuze (A t/m E) → bepaalt de schaal binnen de gekozen familie.
  // Niet meer aanroepbaar via UI-knop; volgt automatisch uit criteria-scoring.
  const kiesVariant = React.useCallback((letter) => {
    const schaal = huidigeRol?.variant_schalen?.[letter];
    if (!schaal) return;
    setNiveau(schaal);
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, festivaltype: letter, niveau_bron: "variant" } : r
      ),
    }));
  }, [huidigeRol?.variant_schalen, setState]);

  // Auto-apply: bij elke nieuwe variant-aanbeveling de schaal direct overnemen.
  React.useEffect(() => {
    if (!hulpResultaat?.variant) return;
    if (huidigeRol?.festivaltype === hulpResultaat.variant) return;
    kiesVariant(hulpResultaat.variant);
  }, [hulpResultaat?.variant, huidigeRol?.festivaltype, kiesVariant]);

  const huidige = tariefrijen.find(t => t.niveau === niveau);
  const isValide = !!huidige;
  const bevestig = () => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) =>
        i === 0 ? { ...r, niveau, niveau_bron: huidigeRol?.niveau === niveau ? "rol-lookup" : "handmatig" } : r
      ),
    }));
    next();
  };

  // Routing: 3.1 = variant-sub-pagina, 3.2 = schaal-sub-pagina.
  if (subStep === "variant") {
    return (
      <VariantSubPagina
        huidigeRol={huidigeRol}
        familieCriteria={familieCriteria}
        antwoorden={hulpAntwoorden}
        resultaat={hulpResultaat}
        tariefrijen={tariefrijen}
        vorm={state.vorm}
        subFamilieId={subFamilieId}
        onKiesSubFamilie={setSubFamilieId}
        onBeantwoord={beantwoordCriterium}
        onBeantwoordSub={beantwoordSubCriterium}
        onTerug={back}
        onVerder={() => setSubStep("schaal")}
      />
    );
  }

  return (
    <SchaalSubPagina
      isFestival={isFestival}
      vorm={state.vorm}
      perspectief={state.perspectief}
      isClient={isClient}
      heeftVarianten={heeftVarianten}
      huidigeRol={huidigeRol}
      tariefrijen={tariefrijen}
      gidsNiveaus={gidsNiveaus}
      niveau={niveau}
      setNiveau={setNiveau}
      isValide={isValide}
      huidige={huidige}
      onTerug={heeftVarianten ? () => setSubStep("variant") : back}
      onBevestig={bevestig}
      hideNext={hideNext}
      subOverride={subOverride}
    />
  );
};

// ============================================================
// 3.1: Variant-sub-pagina (alleen festival, alleen heeftVarianten)
// ============================================================
const VariantSubPagina = ({
  huidigeRol, familieCriteria, antwoorden, resultaat,
  tariefrijen, vorm,
  subFamilieId, onKiesSubFamilie,
  onBeantwoord, onBeantwoordSub, onTerug, onVerder,
}) => {
  const letters = VARIANT_LETTERS.filter(l => huidigeRol.variant_schalen[l] != null);
  const kwalitatief = familieCriteria?.type === "kwalitatief";
  const voorstelVariant = resultaat?.variant || null;
  const voorstelSchaal = voorstelVariant ? huidigeRol.variant_schalen[voorstelVariant] : null;
  const voorstelTarief = voorstelSchaal ? tariefrijen.find(t => t.niveau === voorstelSchaal) : null;

  // Resolve actieve sub-familie en de bijbehorende uitgesloten kolommen.
  // Criteria die uitgesloten zijn voor de gekozen sub-familie verbergen we
  // helemaal: gebruiker ziet alleen wat telt voor zijn/haar variant.
  const subFamilie = familieCriteria?.subFamilies
    ? (familieCriteria.subFamilies.find(s => s.id === subFamilieId)
       || familieCriteria.subFamilies.find(s => s.id === familieCriteria.defaultSubFamilieId)
       || familieCriteria.subFamilies[0])
    : null;
  const uitgesloten = new Set(subFamilie?.uitgesloten || []);
  const effectieveCriteria = (familieCriteria?.criteria || []).filter(c => !uitgesloten.has(c.id));

  return (
    <div>
      <StepHeader
        tag="Filmfestival"
        vorm={vorm}
        title={vorm === "loondienst" ? "Welke variant past bij jouw functie?" : "Welke variant past bij jouw rol?"}
        sub={`Het Functiehandboek Referentiefuncties Filmfestivals onderscheidt voor ${huidigeRol.rol} ${letters.length} varianten (${letters.join(", ")}). Beantwoord de vragen hieronder. Daarna zie je welke variant bij jouw ${vorm === "loondienst" ? "functie" : "rol"} past.`}
        onBack={onTerug}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12 }}>
            Je rol: <span style={{ fontWeight: 400 }}>{huidigeRol.rol}</span> · {huidigeRol.dep}
          </div>

          {!familieCriteria && (
            <FwInlineBanner type="warning" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Geen criteria-vragen beschikbaar voor deze rol.</div>
              <div style={{ fontSize: 13 }}>Ga terug en kies een andere rol: of meld dit bij Filmwerkwijs.</div>
            </FwInlineBanner>
          )}

          {familieCriteria?.subFamilies && (
            <div style={{ background: FW.white, borderRadius: 20, padding: 18, border: `1.5px solid ${FW.gray100}`, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>
                Welk soort programma-onderdeel leid je?
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {familieCriteria.subFamilies.map(sf => {
                  const isGekozen = sf.id === (subFamilie?.id || subFamilieId);
                  return (
                    <button key={sf.id} onClick={() => onKiesSubFamilie(sf.id)} style={{
                      background: isGekozen ? FW.navy : FW.white,
                      color: isGekozen ? FW.yellow : FW.navy,
                      border: `1.5px solid ${isGekozen ? FW.navy : FW.gray100}`,
                      borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                      fontFamily: "var(--font-sans)", fontSize: 13, lineHeight: 1.4,
                      textAlign: "left", flex: "1 1 auto",
                    }}>{sf.label}</button>
                  );
                })}
              </div>
              <div style={{ fontSize: 12, color: FW.gray500, marginTop: 10, lineHeight: 1.4 }}>
                Bepaalt de norm en welke criteria meetellen: bron: Functiehandboek Filmfestivals 2.0, p. 17.
              </div>
            </div>
          )}

          {familieCriteria && (
            <div style={{ background: FW.white, borderRadius: 20, padding: 22, border: `1.5px solid ${FW.gray100}`, marginBottom: 16 }}>
              <div style={{ display: "grid", gap: 14 }}>
                {effectieveCriteria.map((c, idx) => (
                  <div key={c.id} style={{ padding: 16, background: FW.cream, borderRadius: 12, border: `1.5px solid ${FW.gray100}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 4 }}>
                      {idx + 1}. {c.label}
                    </div>
                    {c.toelichting && (
                      <div style={{ fontSize: 12, color: FW.gray500, marginBottom: 10, lineHeight: 1.4 }}>{c.toelichting}</div>
                    )}
                    {c.subCriteria ? (
                      <div style={{ display: "grid", gap: 10 }}>
                        {c.subCriteria.map(sc => (
                          <div key={sc.id} style={{
                            background: FW.white,
                            borderRadius: 10,
                            border: `1.5px solid ${FW.gray100}`,
                            padding: 14,
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy2, marginBottom: 8 }}>{sc.label}</div>
                            <div style={{ display: "grid", gap: 6 }}>
                              {sc.keuzes.map((k, ki) => {
                                const isGekozen = antwoorden[c.id]?.sub?.[sc.id]?.niveau === k.niveau;
                                return (
                                  <button key={ki}
                                    onClick={() => onBeantwoordSub(c.id, sc.id, isGekozen ? null : k)}
                                    title={isGekozen ? "Klik om te deselecteren" : undefined}
                                    style={{
                                    background: isGekozen ? FW.navy : FW.cream,
                                    color: isGekozen ? FW.yellow : FW.navy,
                                    border: `1.5px solid ${isGekozen ? FW.navy : FW.gray100}`,
                                    borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                                    fontFamily: "var(--font-sans)", fontSize: 13, lineHeight: 1.4,
                                    textAlign: "left",
                                  }}>{k.label}</button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: 6 }}>
                        {c.keuzes.map((k, ki) => {
                          const isGekozen = kwalitatief
                            ? antwoorden[c.id]?.variant === k.variant
                            : antwoorden[c.id]?.niveau === k.niveau;
                          return (
                            <button key={ki}
                              onClick={() => onBeantwoord(c.id, isGekozen ? null : k)}
                              title={isGekozen ? "Klik om te deselecteren" : undefined}
                              style={{
                              background: isGekozen ? FW.navy : FW.white,
                              color: isGekozen ? FW.yellow : FW.navy,
                              border: `1.5px solid ${isGekozen ? FW.navy : FW.gray100}`,
                              borderRadius: 10, padding: "10px 14px", cursor: "pointer",
                              fontFamily: "var(--font-sans)", fontSize: 13, lineHeight: 1.4,
                              textAlign: "left",
                            }}>{k.label}</button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {voorstelVariant && (
            <div style={{
              background: FW.taupe, borderRadius: 20, padding: 22,
              border: `1.5px solid ${FW.taupe}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Aanbeveling op basis van je antwoorden
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: FW.navy }}>
                Variant {voorstelVariant}
                {voorstelSchaal != null && (
                  <span style={{ fontSize: 14, fontWeight: 500, color: FW.navy2, marginLeft: 8 }}>
                    schaal {voorstelSchaal}{voorstelTarief && ` · ${calc.fmt(voorstelTarief.tarief_min)}/u`}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: FW.navy2, marginTop: 6, lineHeight: 1.5 }}>
                De functionaris dient aan tenminste <b>{resultaat.vereist} van de {resultaat.totaal} criteria</b> te voldoen om in een salarisschaal te worden ingedeeld.
              </div>
            </div>
          )}

          {!voorstelVariant && familieCriteria && resultaat?.beantwoord === effectieveCriteria.length && (
            <div style={{
              background: "#FEF9B3", borderRadius: 20, padding: 22,
              border: `1.5px solid ${FW.yellow}`,
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy2, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="ri-error-warning-line" /> Geen duidelijke variant
              </div>
              <div style={{ fontSize: 13, color: FW.navy2, lineHeight: 1.5 }}>{resultaat.uitleg}</div>
            </div>
          )}
        </div>

        <div className="aside-card" style={{ display: "grid", gap: 16, position: "sticky", top: 20 }}>
          <div style={{ background: "#E7FFF2", borderRadius: 20, padding: 22 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <i className="ri-stack-line" /> Varianten {letters.join("/")}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: FW.navy2 }}>
              Het Functiehandboek Filmfestivals onderscheidt per functie meerdere varianten, voor <b>{huidigeRol.rol}</b> zijn dat <b>{letters.join(", ")}</b>. <b>{letters[0]}</b> is de laagste schaal; hogere letters horen bij meer verantwoordelijkheid en een hoger starttarief.
            </div>
          </div>
          {huidigeRol.functiedoel && (
            <div style={{ background: FW.white, borderRadius: 20, padding: 22, border: `1.5px solid ${FW.gray100}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                Functieomschrijving
              </div>
              <div style={{ fontSize: 14, color: FW.navy2, lineHeight: 1.55 }}>{huidigeRol.functiedoel}</div>
            </div>
          )}
        </div>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={onTerug}>← Terug</FwButton>
        <FwButton variant="primary" onClick={onVerder} disabled={!voorstelVariant}>
          {vorm === "loondienst" ? "Verder naar Schaal" : "Verder naar Tarief"}
        </FwButton>
      </div>
    </div>
  );
};

// Eén rij in het tarief-blok voor de opdrachtgever-flow.
// Tabel-stijl: label links, bedrag rechts uitgelijnd, dunne divider boven elke rij.
const ClientTariefRij = ({ label, bedrag, eenheid, tooltip, eerste }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "baseline",
    paddingTop: eerste ? 12 : 10,
    paddingBottom: 6,
    borderTop: `1px solid rgba(34,22,89,.18)`,
  }}>
    <div style={{
      fontSize: 13, fontWeight: 600, color: FW.navy,
      display: "inline-flex", alignItems: "center", gap: 4,
    }}>
      {label}
      {tooltip}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy, lineHeight: 1.1, whiteSpace: "nowrap", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
      {calc.fmt(bedrag)}<span style={{ fontSize: 12, fontWeight: 500 }}>{eenheid}</span>
    </div>
  </div>
);

// Periodieken-tabel voor festival-loondienst op de schaal-pagina.
// 2 kolommen naast elkaar, sequentieel gevuld (links 0..helft, rechts helft..eind)
// zodat het oog van boven naar beneden door de volgorde leest.
const LoondienstPeriodiekenBlok = ({ niveau, rol }) => {
  const rijen = (config.tarieven_festival_loondienst || [])
    .filter(t => t.niveau === niveau)
    .map(t => ({ p: Number(t.periodiek), bruto: Number(t.bruto_fulltime) || 0 }))
    .sort((a, b) => a.p - b.p);
  if (!rijen.length) return null;
  const knip = Math.ceil(rijen.length / 2);
  const kolommen = [rijen.slice(0, knip), rijen.slice(knip)];
  const hdrStyle = {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: 0.5, color: FW.navy, opacity: .6,
  };
  return (
    <div className="fw-card-mobile" style={{ marginTop: 20, padding: 20, background: FW.beige2, borderRadius: 16 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>
          Schaal {niveau}: {rol}
        </div>
        <div style={{ fontSize: 15, fontWeight: 700, color: FW.navy, marginTop: 2, lineHeight: 1.3 }}>
          Bruto fulltime maandsalaris
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 28, fontVariantNumeric: "tabular-nums" }}>
        {kolommen.map((kolom, kIdx) => (
          <div key={kIdx} style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 14, rowGap: 0, alignItems: "baseline" }}>
            <div style={{ ...hdrStyle, paddingBottom: 6, borderBottom: `1px solid rgba(34,22,89,.22)` }}>Periodiek</div>
            <div style={{ ...hdrStyle, paddingBottom: 6, borderBottom: `1px solid rgba(34,22,89,.22)`, textAlign: "right" }}>Bruto/mnd</div>
            {kolom.map(({ p, bruto }, i) => (
              <React.Fragment key={p}>
                <div style={{
                  fontSize: 13, fontWeight: 600, color: FW.navy,
                  padding: "7px 0",
                  borderTop: i === 0 ? "none" : `1px solid rgba(34,22,89,.10)`,
                }}>Periodiek {p}</div>
                <div style={{
                  fontSize: 14, fontWeight: 700, color: FW.navy,
                  padding: "7px 0", textAlign: "right", whiteSpace: "nowrap",
                  borderTop: i === 0 ? "none" : `1px solid rgba(34,22,89,.10)`,
                }}>{calc.fmt(bruto)}</div>
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================
// 3.2: Schaal-sub-pagina (zwaarteniveau-keuze)
// ============================================================
const SchaalSubPagina = ({
  isFestival, vorm, perspectief, isClient, heeftVarianten, huidigeRol, tariefrijen, gidsNiveaus,
  niveau, setNiveau, isValide, huidige,
  onTerug, onBevestig, hideNext = false, subOverride,
}) => {
  const headerTag = isFestival ? "Filmfestival" : "Film/AV-productie";
  const headerTitle = isClient
    ? "Welk niveau en starttarief passen bij deze opdracht?"
    : (isFestival && vorm === "loondienst")
      ? "Welke schaal hoort bij jouw functie?"
      : "Welk niveau past bij jouw opdracht?";
  // TODO: vervang '#' door definitieve URL van de Leidraad Veilig en Gezond Werken.
  const headerSub = isFestival
    ? (heeftVarianten
        ? `Je antwoorden passen bij ${huidigeRol.rol}${huidigeRol.festivaltype ? ` ${huidigeRol.festivaltype}` : ""}.`
        : "De Fair Pay richtlijn voor filmfestivals hanteert 12 schalen (gebaseerd op Cao Toneel & Dans). Voor deze rol staat de schaal vast.")
    : isClient
      ? (
          <>
            Het Opdracht Kompas deelt werkzaamheden in op zwaarteniveau (niveaus A t/m K). Elk zwaarteniveau heeft een eigen minimum starttarief. Hieronder zie je het voorgestelde zwaarteniveau voor deze opdracht en de bijbehorende uur- en dagtarieven. Meer informatie over werk- en rusttijden vind je in de <a href="https://www.filmwerkwijs.nl/afspraken-productie/wat-zijn-de-regels-voor-werken-op-een-productie" target="_blank" rel="noreferrer" style={{ color: FW.navy, fontWeight: 700, textDecoration: "underline" }}>Leidraad Veilig en Gezond Werken</a>, bij Rust- en werktijden &amp; planning.
          </>
        )
      : "Het Opdracht Kompas deelt werkzaamheden in op zwaarteniveau (niveaus A t/m K). Elk zwaarteniveau heeft een eigen minimum starttarief. Hieronder staat het zwaarteniveau dat bij jouw werkzaamheden hoort. Klopt het niveau niet? Pas het handmatig één niveau lager aan als dat van toepassing is, denk daarbij aan de complexiteit van de productie en de benodigde kennis en ervaring. Het zwaarteniveau en bijbehorende starttarief zijn de ondergrens voor de onderhandelingen.";
  return (
    <div>
      <StepHeader
        tag={headerTag}
        vorm={isFestival ? vorm : undefined}
        perspectief={!isFestival ? perspectief : undefined}
        title={headerTitle}
        sub={subOverride ?? headerSub}
        onBack={onTerug}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          {!isFestival && (
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 12 }}>
              {isClient ? "De Rol" : "Je rol"}: <span style={{ fontWeight: 400 }}>{huidigeRol.rol}</span> · {huidigeRol.dep}
              {gidsNiveaus.length > 1 && (
                <span style={{ marginLeft: 10, fontSize: 12, color: FW.navy2, fontWeight: 500 }}>
                  Volgens de Gids past deze rol bij niveau {gidsNiveaus.join(", ")}.
                </span>
              )}
            </div>
          )}

          <div style={{ background: FW.white, borderRadius: 20, padding: 20, border: `1.5px solid ${FW.gray100}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, marginBottom: 14, display: "flex", justifyContent: "space-between" }}>
              <span>{isFestival
                ? `Schaal bij deze ${vorm === "loondienst" ? "functie" : "rol"}`
                : "Zwaarteniveau bij deze rol"}</span>
              <span style={{ color: FW.gray500, fontWeight: 500 }}>{isFestival ? "Schaal 1 → 12" : "Schaal A → K"}</span>
            </div>
            {(() => {
              const ascending = tariefrijen.slice().reverse();
              const ascendingNiveaus = ascending.map(t => t.niveau);
              // Bepaal welke niveaus aanklikbaar zijn. Alle overige niveaus
              // tonen we wél (incl. bedrag op hover) maar zijn niet selecteerbaar.
              //  - Film/AV: de Gids-niveaus + precies één niveau lager dan het
              //    laagste Gids-niveau (bv. Gids F/G → ook E).
              //  - Festival zonder varianten: de Gids-schaal(en) (bv. 5 & 6).
              //  - Festival met varianten: alléén de via de criteria bepaalde
              //    schaal (de huidige selectie); een functie hoort bij één
              //    vaste schaal, de overige variant-schalen zijn niet klikbaar.
              const gidsIdxs = gidsNiveaus.map(n => ascendingNiveaus.indexOf(n)).filter(i => i >= 0);
              const laagsteGidsIdx = gidsIdxs.length ? Math.min(...gidsIdxs) : -1;
              const eenLagerNiveau = (!isFestival && laagsteGidsIdx > 0) ? ascendingNiveaus[laagsteGidsIdx - 1] : null;
              const klikbareNiveaus = new Set(
                isFestival
                  ? (heeftVarianten ? (niveau != null ? [niveau] : []) : gidsNiveaus)
                  : [...gidsNiveaus, ...(eenLagerNiveau != null ? [eenLagerNiveau] : [])]
              );
              const tiers = isFestival
                ? [
                    { label: "Operationeel", items: ascending.slice(0, 4) },
                    { label: "Tactisch",     items: ascending.slice(4, 8) },
                    { label: "Strategisch",  items: ascending.slice(8, 12) },
                  ]
                : [
                    { label: "Operationeel", items: ascending.slice(0, 3) },
                    { label: "Tactisch",     items: ascending.slice(3, 7) },
                    { label: "Strategisch",  items: ascending.slice(7, 11) },
                  ];
              return (
                <div className="niveau-tiers" style={{ display: "grid", gridTemplateColumns: isFestival ? "repeat(12,1fr)" : "repeat(11,1fr)", gap: 4 }}>
                  {tiers.map(tier => (
                    <div key={tier.label} className="niveau-tier">
                      <div className="niveau-tier-label">{tier.label}</div>
                      <div className="niveau-tier-buttons">
                        {tier.items.map(r => {
                          const isGekozen = niveau === r.niveau;
                          const passendBijRol = gidsNiveaus.includes(r.niveau);
                          // Niet-klikbare niveaus krijgen een grijze stijl;
                          // klikbare (incl. film/AV één-lager E) krijgen de
                          // "passend"-kleur, gelijk aan de Gids-niveaus.
                          const klikbaar = klikbareNiveaus.has(r.niveau);
                          const heeftBedrag = r.tarief_min != null;
                          const titel = !heeftBedrag
                            ? `${r.niveau}`
                            : `${r.niveau}: ${calc.fmt(r.tarief_min)}/u${klikbaar && passendBijRol ? `: past bij ${huidigeRol.rol}` : ""}`;
                          return (
                            <button key={r.niveau}
                              type="button"
                              onClick={() => { if (klikbaar) setNiveau(r.niveau); }}
                              aria-disabled={!klikbaar}
                              tabIndex={klikbaar ? 0 : -1}
                              title={titel}
                              style={{
                                background: isGekozen ? FW.navy : (klikbaar ? FW.beige2 : FW.gray100),
                                color: isGekozen ? "#fff" : (klikbaar ? FW.navy : FW.gray500),
                                border: `1.5px solid ${isGekozen ? FW.navy : (klikbaar ? FW.taupe : FW.gray100)}`,
                                borderRadius: 10, padding: "14px 4px",
                                cursor: klikbaar ? "pointer" : "not-allowed",
                                fontFamily: "var(--font-sans)", fontWeight: 700, fontSize: 18,
                                transition: "all .15s",
                                opacity: klikbaar ? 1 : 0.6,
                              }}>{r.niveau}</button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="niveau-legend-desktop" style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 12, color: FW.gray500 }}>
              <span>Operationeel</span>
              <span>Tactisch</span>
              <span>Strategisch</span>
            </div>
          </div>

          {isValide && isFestival && vorm === "loondienst" && (
            <LoondienstPeriodiekenBlok niveau={niveau} rol={huidigeRol.rol} />
          )}

          {isValide && !(isFestival && vorm === "loondienst") && (
            <div className="fw-card-mobile" style={{ marginTop: 20, padding: 18, background: FW.beige2, borderRadius: 16 }}>
              {isClient ? (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>
                      Niveau {huidige.niveau}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: FW.navy, marginTop: 2, lineHeight: 1.3 }}>
                      {capDomein(huidige.domein)}
                    </div>
                  </div>
                  <ClientTariefRij
                    label="Minimum starttarief"
                    bedrag={huidige.tarief_min}
                    eenheid="/uur"
                    tooltip={huidige.tarief_max != null && (
                      <RolTooltip
                        color={FW.navy}
                        ariaLabel="Bovenkant referentieschaal"
                        tekst={`Voor dit niveau geldt een bovenkant referentieschaal van ${calc.fmt(huidige.tarief_max)}/uur.`}
                      />
                    )}
                    eerste
                  />
                  <ClientTariefRij
                    label="Minimum dagtarief (8,5 u)"
                    bedrag={huidige.tarief_min * 8.5}
                    eenheid="/dag"
                    tooltip={
                      // TODO: leidraad-tooltip omzetten naar klikbare link zodra URL bekend is (zie TODO regel ~525).
                      <RolTooltip
                        color={FW.navy}
                        ariaLabel="Werk- en rusttijden"
                        tekst="Zie de uitgangspunten voor werk- en rusttijden en planning vanaf punt 19 in de Leidraad veilig en gezond werken."
                      />
                    }
                  />
                  <ClientTariefRij
                    label="Minimum dagtarief (9,5 u)"
                    bedrag={huidige.tarief_min * 9.5}
                    eenheid="/dag"
                    tooltip={
                      <RolTooltip
                        color={FW.navy}
                        ariaLabel="Werk- en rusttijden"
                        tekst="Zie de uitgangspunten voor werk- en rusttijden en planning vanaf punt 19 in de Leidraad veilig en gezond werken."
                      />
                    }
                  />
                </>
              ) : isFestival ? (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "baseline" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>Niveau {huidige.niveau}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: FW.navy, marginTop: 2, lineHeight: 1.3 }}>
                        {huidigeRol.rol}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{
                        fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
                        color: FW.navy, opacity: .75,
                      }}>
                        Minimum starttarief
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                        {calc.fmt(huidige.tarief_min)}<span style={{ fontSize: 12, fontWeight: 500 }}>/uur</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: FW.navy, opacity: .8, marginTop: 2, whiteSpace: "nowrap" }}>
                        {calc.fmt(huidige.tarief_min * 7.6)}<span style={{ fontSize: 11, fontWeight: 500 }}>/dag (× 7,6 u)</span>
                      </div>
                    </div>
                  </div>
                  {/* Bovenkant referentieschaal (zzp): verschijnt zodra tarief_max in de
                      admin is ingevuld; bron is de festival-salaristabel, niet het Opdracht Kompas. */}
                  {huidige.tarief_max != null && (
                    <div style={{ fontSize: 12, color: FW.navy2, lineHeight: 1.45, padding: "10px 0 2px", borderTop: `1px solid rgba(34,22,89,.12)`, marginTop: 10 }}>
                      Voor dit niveau geldt een bovenkant referentieschaal van {calc.fmt(huidige.tarief_max)}/uur.
                    </div>
                  )}
                </>
              ) : (
                // Film/AV ZZP: starttarief + bovenkant referentieschaal (permanent in
                // beeld, geen tooltip) + dagtarieven omgerekend naar 8,5 en 9,5 uur.
                <>
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy, opacity: .75 }}>
                      Niveau {huidige.niveau}
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: FW.navy, marginTop: 2, lineHeight: 1.3 }}>
                      {capDomein(huidige.domein)}
                    </div>
                  </div>
                  <ClientTariefRij
                    label="Minimum starttarief"
                    bedrag={huidige.tarief_min}
                    eenheid="/uur"
                    eerste
                  />
                  {huidige.tarief_max != null && (
                    <div style={{ fontSize: 12, color: FW.navy2, lineHeight: 1.45, padding: "8px 0 2px" }}>
                      Voor dit niveau geldt een bovenkant referentieschaal van {calc.fmt(huidige.tarief_max)}/uur.
                    </div>
                  )}
                  <ClientTariefRij
                    label="Minimum dagtarief (8,5 u)"
                    bedrag={huidige.tarief_min * 8.5}
                    eenheid="/dag"
                    tooltip={
                      // TODO: leidraad-tooltip omzetten naar klikbare link zodra URL bekend is (zie TODO regel ~525).
                      <RolTooltip
                        color={FW.navy}
                        ariaLabel="Werk- en rusttijden"
                        tekst="Zie de uitgangspunten voor werk- en rusttijden en planning vanaf punt 19 in de Leidraad veilig en gezond werken."
                      />
                    }
                  />
                  <ClientTariefRij
                    label="Minimum dagtarief (9,5 u)"
                    bedrag={huidige.tarief_min * 9.5}
                    eenheid="/dag"
                    tooltip={
                      <RolTooltip
                        color={FW.navy}
                        ariaLabel="Werk- en rusttijden"
                        tekst="Zie de uitgangspunten voor werk- en rusttijden en planning vanaf punt 19 in de Leidraad veilig en gezond werken."
                      />
                    }
                  />
                </>
              )}
            </div>
          )}

          {!isValide && (
            <div style={{ marginTop: 20, padding: 24, background: FW.beige2, borderRadius: 20, border: `1.5px dashed ${FW.gray500}`, textAlign: "center", color: FW.navy2 }}>
              <i className="ri-arrow-up-line" style={{ fontSize: 22 }} />
              <div style={{ fontSize: 15, marginTop: 6, fontWeight: 600 }}>Kies een niveau in het schema hierboven om door te gaan.</div>
            </div>
          )}

          {isValide && !isFestival && (() => {
            const bullets = bulletsVoorRol(huidigeRol.dep, huidigeRol.rol);
            if (!bullets?.length) return null;
            return (
              <div style={{ marginTop: 12, background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, padding: "18px 22px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: FW.navy, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <i className="ri-compass-3-line" style={{ fontSize: 16 }} />
                  Referentiefunctie: {huidigeRol.rol}
                </div>
                <ul style={{ fontSize: 14, lineHeight: 1.55, color: FW.navy2, margin: 0, paddingLeft: 20 }}>
                  {bullets.map((b, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>{b}</li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {isValide && !isFestival && niveauIndicatorenFilmAv[niveau] && (
            <details style={{ marginTop: 12, background: FW.white, border: `1.5px solid ${FW.gray100}`, borderRadius: 14, padding: "12px 18px" }}>
              <summary style={{ cursor: "pointer", fontSize: 14, fontWeight: 700, color: FW.navy, listStyle: "none", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="ri-arrow-right-s-line" style={{ fontSize: 18, transition: "transform .15s" }} />
                Niveau-indicatoren voor zelfstandig werkenden: niveau {niveau}
                <span style={{ marginLeft: "auto", fontSize: 12, fontWeight: 500, color: FW.gray500 }}>USB-punten {niveauIndicatorenFilmAv[niveau].usb}</span>
              </summary>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${FW.gray100}`, fontSize: 14, lineHeight: 1.55, color: FW.navy2 }}>
                {Object.entries(niveauIndicatorenFilmAv[niveau].secties).map(([key, tekst]) => (
                  <div key={key} style={{ marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, color: FW.navy, marginBottom: 3 }}>{sectieLabels[key]}</div>
                    <div>{tekst}</div>
                  </div>
                ))}
              </div>
            </details>
          )}

        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10 }}>
            {isFestival ? "Hoe werkt de schaal?" : "Hoe werkt het zwaarteniveau in het Opdracht Kompas?"}
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.5, color: FW.navy2 }}>
            {isFestival ? (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, alignItems: "baseline" }}>
                  <div style={{ fontWeight: 700, color: FW.navy }}>1 – 4</div>
                  <div>Operationeel <span style={{ color: FW.gray500 }}>(office-medewerker, ondersteunend)</span></div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>5 – 8</div>
                  <div>Tactisch <span style={{ color: FW.gray500 }}>(medewerker, coördinator, programmeur, hoofd A/B)</span></div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>9 – 12</div>
                  <div>Strategisch <span style={{ color: FW.gray500 }}>(controller, hoofd C, algemeen/zakelijk/artistiek directeur)</span></div>
                </div>
                <div style={{
                  marginTop: 14, paddingTop: 14,
                  borderTop: `1px solid rgba(34,22,89,.12)`,
                  fontSize: 14, lineHeight: 1.5, color: FW.navy2,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 6 }}>Goed om te weten</div>
                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    De Referentiefuncties in het Functiehandboek Filmfestivals zijn voorbeeldfuncties. Aan elke referentiefunctie is een salarisniveau gekoppeld (1 t/m 12).{vorm === "loondienst" ? "" : " Deze schalen zijn afgeleid van de Cao Toneel en Dans."}
                  </p>
                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    Referentiefuncties zijn geen vaste norm. Wijkt een functie in de praktijk af? Dan vergelijk je die functie met één of meer referentiefuncties. Op basis van de {vorm === "loondienst" ? "daadwerkelijke " : ""}inhoud en verantwoordelijkheden kan een functie hoger of lager worden ingeschaald.
                  </p>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    Komt een functie overeen met een referentiefunctie? Dan wordt die ingeschaald volgens het functieraster Filmfestivals.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 8, alignItems: "baseline" }}>
                  <div style={{ fontWeight: 700, color: FW.navy }}>A, B, C</div>
                  <div>Operationeel</div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>D, E</div>
                  <div>Tactisch/Operationeel</div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>F, G</div>
                  <div>Tactisch</div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>H, I</div>
                  <div>Strategisch/Tactisch</div>

                  <div style={{ fontWeight: 700, color: FW.navy }}>J, K</div>
                  <div>Strategisch</div>
                </div>
                <div style={{
                  marginTop: 14, paddingTop: 14,
                  borderTop: `1px solid rgba(34,22,89,.12)`,
                  fontSize: 14, lineHeight: 1.5, color: FW.navy2,
                }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 6 }}>Goed om te weten</div>
                  <p style={{ margin: "0 0 8px", fontSize: 14 }}>
                    In het <b>Opdracht Kompas</b> zijn per departement <b>referentieopdrachten</b> opgenomen. Daarnaast vind je een overzicht van <b>algemene niveau-indicatoren</b> (A t/m K) die helpen bij het bepalen van de zwaarte van een opdracht. Deze indicatoren hebben betrekking op verschillende factoren, zoals de benodigde <b>kennis en ervaring</b>, de <b>complexiteit van het werk</b>, de aard van de <b>problemen</b> of <b>uitdagingen</b>, de omvang van het <b>takenpakket</b> en de doelgroep waarmee wordt <b>gecommuniceerd</b>.
                  </p>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    De referentieopdrachten zijn ingedeeld op basis van een bepaalde zwaarte. Dit is ook gebeurd met de rollen in de Rollengids. In de praktijk zal een opdracht die daar enigszins van verschilt een niveau <b>hoger of lager</b> kunnen worden ingeschaald. Zo kan bij een{" "}
                    <b style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      grote of complexe productie
                      <RolTooltip
                        ariaLabel="Definitie grote/complexe audiovisuele productie"
                        tekst="Grote/complexe audiovisuele productie: Belangrijke factoren zijn: het type audiovisuele productie, het afbreukrisico (financieel/reputatie), het budget, wijze van financiering (nationaal/internationaal), locaties (binnen/buiten), draaidagen (aantal en verschillende (internationale) locaties), omvang van het team. (Bron: Opdracht Kompas, Begrippenlijst)"
                      />
                    </b>{" "}
                    het zwaarteniveau een niveau hoger uitvallen. Dat is onderwerp van <b>overleg</b> en <b>onderhandeling</b>.
                  </p>
                </div>
              </>
            )}
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        {onTerug
          ? <FwButton variant="ghost" arrow={false} onClick={onTerug}>← Terug</FwButton>
          : <div />}
        {!isClient && !hideNext && (
          <FwButton variant="primary" onClick={onBevestig} disabled={!isValide}>
            {!isFestival
              ? "Ga naar je Openingsbod"
              : vorm === "loondienst"
                ? "Verder naar Dienstverband"
                : "Verder naar Werkdagen"}
          </FwButton>
        )}
      </div>
    </div>
  );
};
